<script setup lang="ts">
import FormLabel from '@/components/FormLabel/FormLabel.vue';
import { getPrefixedUuid } from '@/components/forms/utils';
import { getColorKeyByValue } from '@/styles/vuetify.ts';
import { computed, ref, useAttrs } from 'vue';
import { useTheme } from 'vuetify';
import { VTextField } from 'vuetify/components';
import FormInputDisplay from '../display/FormInputDisplay.vue';
import type { TextFieldProps } from '../types';
import { ensureUniqueClasses } from '@/utils/stringUtils';

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<TextFieldProps>(), {
  mode: 'edit',
});

const passdownProps = computed(() => ({ ...props, label: undefined }));

const attrs = useAttrs();

const passdownAttrs = computed(() => ({
  ...attrs,
  class: undefined,
}));

const classList = computed(() => {
  let textFieldClasses = attrs.class ? (attrs.class as string) : '';

  textFieldClasses = textFieldClasses.concat(' text-field');
  if (props.mode === 'details') {
    textFieldClasses = textFieldClasses.concat(' d-flex');
  }
  return ensureUniqueClasses(textFieldClasses);
});

const theme = useTheme();

const id = props.id ? props.id : getPrefixedUuid('textfield');

const emits = defineEmits(['update:modelValue']);

const hasFocus = ref(false);

const errorMessageDisplay = computed(() => (hasFocus.value ? undefined : props.errorMessage));

function onUpdate(input: string | null) {
  emits('update:modelValue', input || props.emptyValue);
}

const slots = defineSlots<{ [key: string]: unknown }>();
</script>

<template>
  <div :class="classList">
    <FormLabel
      :for="id"
      :infoMessage="infoMessage"
      :label="label"
      :maxRows="1"
      :disabled="props.disabled"
    >
      <template #default="{ onBlur, onFocus, hoverProps }">
        <FormInputDisplay
          v-if="mode === 'details'"
          :class="
            passdownProps.color
              ? `text-${getColorKeyByValue(passdownProps.color, theme.current.value.colors)}`
              : undefined
          "
          :value="modelValue"
          :suffix="suffix"
          :prefix="prefix"
        />
        <v-text-field
          v-else
          v-bind="{ ...passdownProps, ...passdownAttrs, ...hoverProps, id }"
          :modelValue="modelValue"
          :error="Boolean(errorMessageDisplay)"
          :hint="errorMessageDisplay || hint"
          :persistentHint="Boolean(errorMessageDisplay) || persistentHint"
          @update:modelValue="onUpdate"
          @focus="
            hasFocus = true;
            onFocus();
          "
          @blur="
            hasFocus = false;
            onBlur();
          "
          @keydown.enter.prevent="
            hasFocus = false;
            onBlur();
          "
        >
          <template v-for="(_, name) in slots as {}" #[name]="slotData">
            <slot :name="name" v-bind="slotData as any" />
          </template>
        </v-text-field>
      </template>
    </FormLabel>
  </div>
</template>

<style scoped lang="scss">
:deep(.v-text-field) {
  svg {
    fill: rgb(var(--v-theme-secondary));
  }

  .v-input__append button {
    &:disabled {
      svg {
        fill: currentColor;
      }
    }
  }

  .v-text-field__suffix {
    opacity: 1;
  }

  .v-text-field__prefix {
    opacity: 1;
  }
}
</style>
